export function filterEmptyValuesFromObject(
  obj: Record<string, string>
): Record<string, string> | null {
  const filteredObj: Record<string, string> = {};
  for (const key in obj) {
    if (obj[key]) {
      filteredObj[key] = obj[key];
    }
  }
  if (!Object.keys(filteredObj).length) {
    return null;
  }
  return filteredObj;
}

export function getObjectKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

export function getPropertyName<T>(o: T, val: keyof T) {
  for (const prop in o) {
    if (prop.toLowerCase() === val.toString().toLowerCase()) {
      return prop;
    }
  }
  return null;
}

export function deepCloneObject<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function copyAndPatchObject<T extends object>(target: T, source: Partial<T>): T {
  const cloned = deepCloneObject(target);
  patchObject(cloned, source);
  return cloned;
}

export function patchObject<T extends object>(target: T, source: Partial<T>): void {
  for (const key of Object.keys(target) as (keyof T)[]) {
    if (source[key] !== undefined) {
      target[key] = source[key]!;
    }
  }
}

export function updateObjectProperty<T extends object, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K]
) {
  obj[key] = value;
}

export function hasKey<T extends object>(obj: T, key: unknown): boolean {
  return !!getObjectKeys(obj).find((k: keyof T): boolean => k === key);
}

export function camelCaseObjectKeys<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => camelCaseObjectKeys(item)) as T;
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const normalizedKey = (key.length ? key[0].toLowerCase() + key.slice(1) : key) as keyof T;
      acc[normalizedKey] = camelCaseObjectKeys(obj[key as keyof T]);
      return acc;
    }, {} as T);
  }
  return obj;
}

export function groupArrayByField<T>(array: T[], field: keyof T): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  array.forEach((item: T) => {
    if (!result[item[field] + '']) {
      result[item[field] + ''] = [];
    }
    result[item[field] + ''].push(item);
  });
  return result;
}

export function trimWhitespaceFromArray(array: string[]): string[] {
  return array.map((item: string): string => item?.trim());
}

export function compareObjects<T>(obj1: T, obj2: T): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export function filterDuplicateValuesFromArray<T>(
  originalArray: T[],
  arrayWithDuplicates: T[]
): T[] {
  return originalArray.filter((item: T): boolean => {
    const exists = arrayWithDuplicates.find((itemToBeRemoved: T): boolean => {
      return compareObjects(item, itemToBeRemoved);
    });

    return !exists;
  });
}

export function filterArrayByKey<T extends object>(array: T[], filter: T[], key: keyof T): T[] {
  const filtered = array
    .map((item: T): T | null => {
      const exists = findItemInArrayByObjectKey(filter, key, item);

      return exists ? item : null;
    })
    .filter((code: T | null): code is T => !!code);

  return filtered;
}

export function findItemInArrayByObjectKey<T extends object>(
  array: T[],
  key: keyof T,
  value: T
): T | undefined {
  return array.find((item: T): boolean => item[key] === value[key]);
}

export function removeUndefinedPropertiesFromObject<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  getObjectKeys(obj).forEach((key: keyof T): void => {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  });
  return result;
}

export function parseArrayBuffer<T>(buffer: ArrayBuffer): T | null {
  try {
    const decoder = new TextDecoder('utf-8');
    const decoded = decoder.decode(buffer);
    const parsed = JSON.parse(decoded);

    return parsed;
  } catch {
    return null;
  }
}

export async function parseBlob<T>(blob: Blob): Promise<T | null> {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);

    return parsed;
  } catch {
    return null;
  }
}

export function parseJson<T>(json: string, defaultValue?: T): T | null {
  try {
    const parsed = JSON.parse(json);
    return parsed as T;
  } catch {
    return defaultValue || null;
  }
}

export function removeKeysFromObject<T extends object>(
  obj: T,
  keys: (keyof T)[]
): Partial<T> {
  const result: Partial<T> = {};
  getObjectKeys(obj).forEach((key: keyof T): void => {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  });
  return result;
}

export function removeKeysFromArrayObjects<T extends Record<string, unknown>>(
  array: T[],
  keys: (keyof T)[]
): Partial<T>[] {
  return array.map((obj: T): Partial<T> => removeKeysFromObject(obj, keys));
}

export function compareObjectsByFields<T>(obj1: T, obj2: T, fields: (keyof T)[]) {
  if (!fields.length) {
    return false;
  }
  return fields.every((field: keyof T): boolean => {
    return obj1[field] === obj2[field];
  });
}

export function compareObjectsByFirstObjectValues<T extends Record<string, unknown>>(obj1: T, obj2: T) {
  const fields = getObjectKeys(obj1);
  if (!fields.length) {
    return false;
  }
  return fields.every((field: keyof T): boolean => {
    if ((typeof obj1[field] === 'object') && obj1[field] !== null) {
      return compareObjectsByFirstObjectValues(obj1[field] as Record<string, unknown>, obj2[field] as Record<string, unknown>);
    }

    return obj1[field] === obj2[field];
  });
}

export function compareArraysOfObjectsByFirstObjectValues<T extends Record<string, unknown>>(arr1: T[], arr2: T[]) {
  return arr1.every((obj1: T, index: number): boolean => {
    return compareObjectsByFirstObjectValues(obj1, arr2[index]);
  });
}
